import Editor from "@monaco-editor/react";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  MenuItem,
  Modal,
  Divider as MUIDivider,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { API_URL } from "../../api/auth";
import { useApiMutation, useApiQuery } from "../../hooks";
import type { APIKey } from "../../types";
import { DataGrid } from "../data-grid";

interface Endpoint {
  method: "GET" | "POST" | "PATCH";
  path: string;
  description: string;
  category?: string;
  params?: Array<{ name: string; type: string; description: string }>;
  body?: Record<string, unknown>;
}

interface EndpointSection {
  category: string;
  endpoints: Endpoint[];
}

interface AppConfig {
  id: string;
  name: string;
}

const API_V1_ENDPOINTS: EndpointSection[] = [
  {
    category: "App Configs",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/app-configs",
        description: "List app configurations",
        params: [
          {
            name: "limit",
            type: "integer",
            description: "Max results (default: 100)",
          },
          {
            name: "offset",
            type: "integer",
            description: "Results offset (default: 0)",
          },
          { name: "q", type: "string", description: "Search query" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/app-configs/{configId}",
        description: "Get a specific app configuration",
      },
      {
        method: "PATCH",
        path: "/api/v1/app-configs/{configId}",
        description: "Update app configuration metadata",
      },
    ],
  },
  {
    category: "Tests",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/tests",
        description: "List tests",
        params: [
          {
            name: "limit",
            type: "integer",
            description: "Max results (default: 100)",
          },
          {
            name: "offset",
            type: "integer",
            description: "Results offset (default: 0)",
          },
          { name: "q", type: "string", description: "Search query" },
          {
            name: "run_status",
            type: "string",
            description: "Filter by run status",
          },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/tests/{testId}",
        description: "Get a specific test",
      },
    ],
  },
  {
    category: "Test Runs",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/test-runs",
        description: "List test runs",
        params: [
          { name: "test_id", type: "string", description: "Filter by test" },
          {
            name: "suite_id",
            type: "string",
            description: "Filter by test suite",
          },
          {
            name: "run_status",
            type: "string",
            description: "Filter by run status",
          },
          {
            name: "limit",
            type: "integer",
            description: "Max results (default: 100)",
          },
          {
            name: "offset",
            type: "integer",
            description: "Results offset (default: 0)",
          },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/test-runs/{runId}",
        description: "Get a specific test run",
      },
    ],
  },
  {
    category: "Test Suites",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/test-suites",
        description: "List test suites",
        params: [
          {
            name: "limit",
            type: "integer",
            description: "Max results (default: 100)",
          },
          {
            name: "offset",
            type: "integer",
            description: "Results offset (default: 0)",
          },
          { name: "q", type: "string", description: "Search query" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/test-suites/{suiteId}",
        description: "Get a specific test suite",
      },
    ],
  },
  {
    category: "Test Suite Runs",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/test-suite-runs",
        description: "List test suite runs",
        params: [
          {
            name: "test_suite_id",
            type: "string",
            description: "Filter by test suite",
          },
          {
            name: "run_status",
            type: "string",
            description: "Filter by run status",
          },
          {
            name: "limit",
            type: "integer",
            description: "Max results (default: 100)",
          },
          {
            name: "offset",
            type: "integer",
            description: "Results offset (default: 0)",
          },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/test-suite-runs/{suiteRunId}",
        description: "Get a specific test suite run",
      },
    ],
  },
  {
    category: "Actions",
    endpoints: [
      {
        method: "POST",
        path: "/api/v1/tests-actions/run",
        description: "Batch run multiple tests",
        body: {
          test_ids: ["test-id-1", "test-id-2"],
          app_config_id: "optional-override",
          variables: [
            { key: "username", value: "alice@example.com" },
            { key: "base_url", value: "https://staging.example.com" },
          ],
        },
      },
      {
        method: "POST",
        path: "/api/v1/test-suites-actions/run",
        description: "Run a test suite",
        body: { suite_id: "suite-id" },
      },
      {
        method: "POST",
        path: "/api/v1/check-schedules-actions/run",
        description:
          "Run a check schedule as an ordered, variable-passing chain. Optional variables seed the chain so layer-0 checks can consume them with {{key}} references.",
        body: {
          schedule_id: "schedule-id",
          variables: [
            { key: "env", value: "staging" },
            { key: "base_url", value: "https://staging.example.com" },
          ],
        },
      },
      {
        method: "POST",
        path: "/api/v1/tests-actions/promote",
        description: "Promote tests to a different app config",
        body: {
          test_ids: ["test-id-1", "test-id-2"],
          target_config_id: "target-config-id",
        },
      },
    ],
  },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-tabpanel-${index}`}
      aria-labelledby={`api-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export const ApiTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScope, setNewKeyScope] = useState<"admin" | "api" | "pw_reporter">("api");
  const [newKeyAppConfig, setNewKeyAppConfig] = useState("");
  const [newKeyExpiration, setNewKeyExpiration] = useState("");
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    open: boolean;
    keyId: string;
    keyName: string;
  }>({
    open: false,
    keyId: "",
    keyName: "",
  });
  const [keyViewModal, setKeyViewModal] = useState<{
    open: boolean;
    key: APIKey | null;
    showKey: boolean;
  }>({
    open: false,
    key: null,
    showKey: false,
  });
  const [keyFilterType, setKeyFilterType] = useState<string>("");
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint | null>(null);
  const [selectedApiKey, setSelectedApiKey] = useState<APIKey | null>(null);
  const [responseData, setResponseData] = useState<string>("");
  const [loadingResponse, setLoadingResponse] = useState(false);

  // Fetch API keys and app configs
  const {
    data: apiKeys,
    isLoading: apiKeysLoading,
    refetch: refetchApiKeys,
  } = useApiQuery<APIKey[]>("/api-keys", {});
  const { data: appConfigs } = useApiQuery<{ configs: AppConfig[] }>("/config", {});

  // Mutations
  const createKeyMutation = useApiMutation<
    {
      name: string;
      scope: "admin" | "api" | "pw_reporter";
      app_config_id?: string;
      expiration_date?: string;
    },
    APIKey
  >("POST", "/api-keys");

  const deleteKeyMutation = useApiMutation<unknown, void>("DELETE", "/api-keys/{keyId}");

  const handleCreateKey = async () => {
    if (!newKeyName) {
      enqueueSnackbar("Key name is required", { variant: "error" });
      return;
    }

    try {
      const result = await createKeyMutation.mutateAsync({
        name: newKeyName,
        scope: newKeyScope,
        app_config_id: newKeyAppConfig || undefined,
        expiration_date: newKeyExpiration || undefined,
      });

      if (!result) {
        throw new Error("Failed to create API key");
      }

      setNewKeyName("");
      setNewKeyScope("api");
      setNewKeyAppConfig("");
      setNewKeyExpiration("");
      setOpenCreateDialog(false);
      enqueueSnackbar("API key created successfully", { variant: "success" });
      refetchApiKeys();
    } catch {
      enqueueSnackbar("Failed to create API key", { variant: "error" });
    }
  };

  const handleDeleteKeyClick = (keyId: string, keyName: string) => {
    setDeleteConfirmModal({
      open: true,
      keyId,
      keyName,
    });
  };

  const handleConfirmDelete = async () => {
    const { keyId } = deleteConfirmModal;
    try {
      await deleteKeyMutation.mutateAsync({
        _url: `/api-keys/${keyId}`,
      });
      enqueueSnackbar("API key deleted successfully", { variant: "success" });
      setDeleteConfirmModal({ open: false, keyId: "", keyName: "" });
      refetchApiKeys();
    } catch {
      enqueueSnackbar("Failed to delete API key", { variant: "error" });
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar("Copied to clipboard", { variant: "success" });
  };

  const allEndpoints = useMemo(
    () =>
      API_V1_ENDPOINTS.flatMap((section) =>
        section.endpoints.map((endpoint) => ({
          ...endpoint,
          category: section.category,
        })),
      ),
    [],
  );

  // Clear response when endpoint changes
  useEffect(() => {
    setResponseData("");
  }, []);

  const handleTryItOut = async () => {
    if (!selectedEndpoint || !selectedApiKey) {
      enqueueSnackbar("Please select an endpoint and API key", {
        variant: "error",
      });
      return;
    }

    setLoadingResponse(true);
    try {
      const path = selectedEndpoint.path.replace(/{[\w]+}/g, "YOUR_ID");
      const url = `${API_URL}${path}`;

      const response = await fetch(url, {
        method: selectedEndpoint.method,
        headers: {
          Authorization: `Bearer ${selectedApiKey.key}`,
          "Content-Type": "application/json",
        },
        ...(selectedEndpoint.method !== "GET" && selectedEndpoint.body
          ? { body: JSON.stringify(selectedEndpoint.body) }
          : {}),
      });

      const data = await response.json();
      setResponseData(JSON.stringify(data, null, 2));
      enqueueSnackbar("API call executed successfully", {
        variant: "success",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      setResponseData(
        JSON.stringify({ error: "Failed to execute API call", details: errorMessage }, null, 2),
      );
      enqueueSnackbar(`Failed to execute API call: ${errorMessage}`, {
        variant: "error",
      });
    } finally {
      setLoadingResponse(false);
    }
  };

  const generateCurlExample = (endpoint: Endpoint, apiKey: string) => {
    const path = endpoint.path.replace(/{[\w]+}/g, "YOUR_ID");
    const method = endpoint.method;
    const authHeader = `Authorization: Bearer ${apiKey}`;

    if (method === "GET") {
      return `curl -X ${method} \\
  -H "${authHeader}" \\
  "${API_URL}${path}"`;
    } else {
      const body = endpoint.body ? JSON.stringify(endpoint.body, null, 2) : "{}";
      return `curl -X ${method} \\
  -H "${authHeader}" \\
  -H "Content-Type: application/json" \\
  -d '${body}' \\
  "${API_URL}${path}"`;
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" component="div" mb={2}>
          API
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Use the External API v1 to interact with the application programmatically. All endpoints
          require authentication via Bearer token.
        </Typography>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="API tabs"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab label="Keys" id="api-tab-0" aria-controls="api-tabpanel-0" />
          <Tab label="Docs" id="api-tab-1" aria-controls="api-tabpanel-1" />
        </Tabs>

        {/* Keys Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box mb={4}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 3 }}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button variant="contained" color="primary" onClick={() => setOpenCreateDialog(true)}>
                Create New API Key
              </Button>

              <FormControl sx={{ minWidth: 150, flex: 0 }} size="small">
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={keyFilterType}
                  label="Filter by Type"
                  onChange={(e) => setKeyFilterType(e.target.value)}
                >
                  <MenuItem value="">All Keys</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="api">API</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* All API Keys */}
            {apiKeysLoading ? (
              <CircularProgress />
            ) : !apiKeys || apiKeys.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No API keys yet
              </Typography>
            ) : (
              <DataGrid<APIKey>
                rows={apiKeys.filter((key) => !keyFilterType || key.scope === keyFilterType)}
                columns={[
                  {
                    field: "name",
                    headerName: "Name",
                    flex: 1,
                    minWidth: 150,
                  },
                  {
                    field: "scope",
                    headerName: "Type",
                    width: 100,
                    renderCell: (params) => (
                      <Chip label={params.value || "api"} size="small" variant="outlined" />
                    ),
                  },
                  {
                    field: "app_config_id",
                    headerName: "App Config",
                    flex: 1,
                    minWidth: 150,
                    renderCell: (params) => {
                      if (!params.row.app_config_id) return "Global";
                      const config = appConfigs?.configs?.find(
                        (c) => c.id === params.row.app_config_id,
                      );
                      return config?.name || "Global";
                    },
                  },
                  {
                    field: "created_at",
                    headerName: "Created",
                    flex: 1,
                    minWidth: 120,
                    renderCell: (params) => new Date(params.value).toLocaleDateString(),
                  },
                  {
                    field: "actions",
                    headerName: "Actions",
                    width: 150,
                    sortable: false,
                    renderCell: (params) => (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setKeyViewModal({
                              open: true,
                              key: params.row,
                              showKey: false,
                            });
                          }}
                        >
                          View Key
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteKeyClick(params.row.id, params.row.name);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ),
                  },
                ]}
                minHeight="400px"
              />
            )}
          </Box>
        </TabPanel>

        {/* Docs Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
              gap: 2,
              height: "calc(100vh - 400px)",
            }}
          >
            {/* Left Column: Endpoints List */}
            <Box sx={{ overflow: "auto" }}>
              <Paper
                variant="outlined"
                sx={{
                  height: "100%",
                  overflow: "auto",
                }}
              >
                <List disablePadding>
                  {allEndpoints.map((endpoint, idx) => {
                    const isSelected =
                      selectedEndpoint?.path === endpoint.path &&
                      selectedEndpoint?.method === endpoint.method;
                    return (
                      <React.Fragment key={idx}>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => setSelectedEndpoint(endpoint)}
                          sx={{
                            borderLeft: isSelected ? "4px solid" : "4px solid transparent",
                            borderLeftColor: isSelected ? "primary.main" : "transparent",
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" width="100%">
                            <Chip
                              label={endpoint.method}
                              size="small"
                              color={
                                endpoint.method === "GET"
                                  ? "primary"
                                  : endpoint.method === "POST"
                                    ? "success"
                                    : "warning"
                              }
                            />
                            <Stack direction="column" spacing={0} flex={1} minWidth={0}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {endpoint.path}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {endpoint.category}
                              </Typography>
                            </Stack>
                          </Stack>
                        </ListItemButton>
                        {idx < allEndpoints.length - 1 && <MUIDivider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Paper>
            </Box>

            {/* Right Column: Endpoint Details */}
            <Box sx={{ overflow: "auto" }}>
              <Paper
                variant="outlined"
                sx={{
                  height: "100%",
                  overflow: "auto",
                  p: 2,
                }}
              >
                {selectedEndpoint ? (
                  <Stack spacing={2} sx={{ height: "100%", overflow: "auto", pt: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={selectedEndpoint.method}
                          size="small"
                          color={
                            selectedEndpoint.method === "GET"
                              ? "primary"
                              : selectedEndpoint.method === "POST"
                                ? "success"
                                : "warning"
                          }
                        />
                        <code style={{ fontSize: "14px", fontWeight: "bold" }}>
                          {selectedEndpoint.path}
                        </code>
                      </Stack>
                      <Stack direction="column" flex={1} sx={{ maxWidth: 200 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>API Key</InputLabel>
                          <Select
                            value={selectedApiKey?.id || ""}
                            label="API Key"
                            onChange={(e) => {
                              const key = apiKeys?.find((k) => k.id === e.target.value);
                              setSelectedApiKey(key || null);
                            }}
                          >
                            <MenuItem value="">None (Use placeholder)</MenuItem>
                            {apiKeys?.map((key: APIKey) => (
                              <MenuItem key={key.id} value={key.id}>
                                {key.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </Stack>

                    <Typography variant="body2">{selectedEndpoint.description}</Typography>

                    <Box>
                      <Typography variant="subtitle2" mb={1}>
                        Example Command
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Editor
                          height="180px"
                          defaultLanguage="bash"
                          value={generateCurlExample(
                            selectedEndpoint,
                            selectedApiKey?.key || "YOUR_API_KEY",
                          )}
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            lineNumbers: "off",
                            wordWrap: "on",
                            wrappingIndent: "same",
                          }}
                        />
                      </Box>
                      <Stack direction="row" spacing={1} mt={1} justifyContent="flex-end">
                        <Button
                          variant="contained"
                          startIcon={<FileCopyIcon />}
                          size="small"
                          onClick={() =>
                            handleCopyToClipboard(
                              generateCurlExample(
                                selectedEndpoint,
                                selectedApiKey?.key || "YOUR_API_KEY",
                              ),
                            )
                          }
                        >
                          Copy
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<PlayArrowIcon />}
                          size="small"
                          onClick={handleTryItOut}
                          disabled={!selectedApiKey || loadingResponse}
                        >
                          {loadingResponse ? "Running..." : "Try it out"}
                        </Button>
                      </Stack>
                    </Box>

                    {selectedEndpoint.method !== "GET" && (
                      <Box
                        sx={{
                          backgroundColor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Typography variant="subtitle2" mb={1}>
                          {selectedEndpoint.method === "POST"
                            ? "Create Resource"
                            : "Update Resource"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedEndpoint.method === "POST"
                            ? "This endpoint creates a new resource. Provide the request body below with the required fields."
                            : "This endpoint updates an existing resource. Modify the request body below with the fields you want to update."}
                        </Typography>
                      </Box>
                    )}

                    {selectedEndpoint.params && selectedEndpoint.params.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" mb={1}>
                          Query Parameters
                        </Typography>
                        <Stack spacing={0.5}>
                          {selectedEndpoint.params.map((param) => (
                            <Typography
                              key={param.name}
                              variant="caption"
                              sx={{
                                p: 0.5,
                                backgroundColor: "grey.100",
                                borderRadius: 0.5,
                              }}
                            >
                              <strong>{param.name}</strong> ({param.type}):
                              {param.description}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {selectedEndpoint.body && (
                      <Box>
                        <Typography variant="subtitle2" mb={1}>
                          Request Body
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Editor
                            height="150px"
                            defaultLanguage="json"
                            value={JSON.stringify(selectedEndpoint.body, null, 2)}
                            options={{
                              readOnly: true,
                              minimap: { enabled: false },
                              lineNumbers: "off",
                            }}
                          />
                        </Box>
                      </Box>
                    )}

                    {responseData && (
                      <Box>
                        <Typography variant="subtitle2" mb={1}>
                          Response
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Editor
                            height="200px"
                            defaultLanguage="json"
                            value={responseData}
                            options={{
                              readOnly: true,
                              minimap: { enabled: false },
                              lineNumbers: "off",
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select an endpoint from the list to view details
                  </Typography>
                )}
              </Paper>
            </Box>
          </Box>
        </TabPanel>
      </CardContent>

      {/* Create API Key Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Key Name"
              fullWidth
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g., CI/CD Pipeline"
            />
            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select
                value={newKeyScope}
                label="Scope"
                onChange={(e) => setNewKeyScope(e.target.value as "admin" | "api")}
              >
                <MenuItem value="api">API (Recommended)</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="pw_reporter">Playwright Reporter</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>App Config (Optional)</InputLabel>
              <Select
                value={newKeyAppConfig}
                label="App Config (Optional)"
                onChange={(e) => setNewKeyAppConfig(e.target.value)}
              >
                <MenuItem value="">None (Global)</MenuItem>
                {appConfigs?.configs?.map((config: AppConfig) => (
                  <MenuItem key={config.id} value={config.id}>
                    {config.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Expiration Date (Optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={newKeyExpiration}
              onChange={(e) => setNewKeyExpiration(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateKey}
            variant="contained"
            disabled={createKeyMutation.isPending || !newKeyName}
          >
            {createKeyMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View API Key Modal */}
      <Modal
        open={keyViewModal.open}
        onClose={() => setKeyViewModal({ open: false, key: null, showKey: false })}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          sx={{
            p: 3,
            maxWidth: 500,
            borderRadius: 1,
            width: "90%",
          }}
        >
          <Typography variant="h6" mb={2}>
            {keyViewModal.key?.name}
          </Typography>

          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "grey.100",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                API Key
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <code
                  style={{
                    fontSize: "14px",
                    wordBreak: "break-all",
                  }}
                >
                  {keyViewModal.showKey ? keyViewModal.key?.key : "••••••••••••••••"}
                </code>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setKeyViewModal({
                        ...keyViewModal,
                        showKey: !keyViewModal.showKey,
                      })
                    }
                    title={keyViewModal.showKey ? "Hide" : "Show"}
                  >
                    {keyViewModal.showKey ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (keyViewModal.key) {
                        handleCopyToClipboard(keyViewModal.key.key);
                      }
                    }}
                    title="Copy to clipboard"
                  >
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>

            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                <strong>Type:</strong> {keyViewModal.key?.scope || "api"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <strong>App Config:</strong>{" "}
                {keyViewModal.key?.app_config_id
                  ? appConfigs?.configs?.find((c) => c.id === keyViewModal.key?.app_config_id)
                      ?.name || "Global"
                  : "Global"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <strong>Created:</strong>{" "}
                {keyViewModal.key?.created_at
                  ? new Date(keyViewModal.key.created_at).toLocaleDateString()
                  : "N/A"}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={3}>
            <Button
              onClick={() => setKeyViewModal({ open: false, key: null, showKey: false })}
              variant="outlined"
            >
              Close
            </Button>
          </Stack>
        </Paper>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmModal.open}
        onClose={() => setDeleteConfirmModal({ open: false, keyId: "", keyName: "" })}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          sx={{
            p: 3,
            maxWidth: 400,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" mb={1}>
            Delete API Key
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Are you sure you want to delete the API key "
            <strong>{deleteConfirmModal.keyName}</strong>"? This action cannot be undone.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              onClick={() => setDeleteConfirmModal({ open: false, keyId: "", keyName: "" })}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={deleteKeyMutation.isPending}
            >
              {deleteKeyMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </Paper>
      </Modal>
    </Card>
  );
};
